import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  BulkEmailResult,
  PROSPECT_EMAIL_BATCH_SIZE,
  Prospect,
  bulkEmailProspects,
} from '../../services/partnerNetwork';
import { toDateInput } from '../../utils/partnerFormat';
import { Alert, Modal, inputClass } from './ui';

interface Props {
  open: boolean;
  prospects: Prospect[];
  onClose: () => void;
  /** Fired after any successful send, so the list can pick up new statuses. */
  onSent: () => void;
}

/**
 * A small starter set, not a template system. The brief is explicit about not
 * overbuilding this in the first version — these are three openings a marketer
 * types constantly, kept editable rather than managed.
 */
const TEMPLATES: Array<{ name: string; subject: string; message: string }> = [
  {
    name: 'Initial partnership outreach',
    subject: 'Partnership opportunity with Nexasolutra',
    message: `I came across your work with hospitality businesses and wanted to introduce Nexasolutra.

We build AI operating systems for hospitality and commerce — the systems restaurants, hotels and retailers run their day on. Consultants and advisers like you often have clients who need exactly this, and we pay a commission on every project that comes through a partner referral.

Would you be open to a short call to see whether it fits the work you already do?`,
  },
  {
    name: 'Follow-up',
    subject: 'Following up — Nexasolutra partnership',
    message: `Just following up on my note about partnering with Nexasolutra.

If it is useful, I can send a one-page summary of how the partner programme works, including the commission structure, so you can look at it in your own time.

Happy to leave it there if the timing is not right.`,
  },
  {
    name: 'Meeting invitation',
    subject: 'A short call about the Nexasolutra partner programme',
    message: `Thanks for your interest in the Nexasolutra partner programme.

Would a 20-minute call suit you this week or next? I will walk through how referrals work end to end, what we handle once a referral comes in, and how commission is calculated and paid.

Let me know a couple of times that work and I will send an invite.`,
  },
];

/**
 * Compose and send to the selected prospects.
 *
 * Sends in small batches through the existing email service rather than one
 * large request: each call has to finish well inside the serverless timeout,
 * and a batch that does time out is reported failed even if some went out, so
 * a small batch keeps that blast radius to a handful of addresses.
 */
const BulkEmailModal: React.FC<Props> = ({ open, prospects, onClose, onSent }) => {
  const { admin } = useAuth();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [markContacted, setMarkContacted] = useState(true);
  const [followUp, setFollowUp] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkEmailResult[] | null>(null);
  const [skipped, setSkipped] = useState(0);

  const sendable = useMemo(
    () => prospects.filter((prospect) => prospect.email),
    [prospects]
  );
  const withoutEmail = prospects.length - sendable.length;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setResults(null);
    setProgress({ done: 0, total: 0 });
    setSkipped(0);
    // A follow-up three days out is the playbook default after first contact.
    const inThreeDays = new Date();
    inThreeDays.setDate(inThreeDays.getDate() + 3);
    setFollowUp(toDateInput(inThreeDays));
  }, [open]);

  const applyTemplate = (index: number) => {
    const template = TEMPLATES[index];
    if (!template) return;
    setSubject(template.subject);
    setMessage(template.message);
  };

  const handleSend = async () => {
    setError(null);
    setResults(null);

    if (!subject.trim() || !message.trim()) {
      setError('A subject and a message are both required.');
      return;
    }

    if (!sendable.length) {
      setError('None of the selected prospects have an email address.');
      return;
    }

    if (!admin?.hasSavedMailPassword) {
      setError(
        'Emails send from your own Zoho mailbox, and you have no mail password saved. Add it in Account first.'
      );
      return;
    }

    const batches: Prospect[][] = [];
    for (let i = 0; i < sendable.length; i += PROSPECT_EMAIL_BATCH_SIZE) {
      batches.push(sendable.slice(i, i + PROSPECT_EMAIL_BATCH_SIZE));
    }

    const collected: BulkEmailResult[] = [];
    let totalSkipped = 0;

    try {
      setSending(true);
      setProgress({ done: 0, total: sendable.length });

      for (const batch of batches) {
        const response = await bulkEmailProspects({
          prospectIds: batch.map((prospect) => prospect.id),
          subject: subject.trim(),
          message: message.trim(),
          markContacted,
          nextFollowUpAt: followUp || null,
        });

        collected.push(...response.results);
        totalSkipped += response.skipped;
        setProgress((current) => ({
          ...current,
          done: current.done + batch.length,
        }));
      }

      setResults(collected);
      setSkipped(totalSkipped);

      if (collected.some((result) => result.success)) onSent();
    } catch (err) {
      // Whatever went out before the failure is still worth showing.
      setResults(collected.length ? collected : null);
      setError(err instanceof Error ? err.message : 'Could not send the emails');
    } finally {
      setSending(false);
    }
  };

  const sent = results?.filter((result) => result.success).length ?? 0;
  const failed = results ? results.length - sent : 0;

  return (
    <Modal
      open={open}
      size="lg"
      title={
        results
          ? 'Sending finished'
          : `Send email to ${sendable.length} ${sendable.length === 1 ? 'contact' : 'contacts'}`
      }
      description={
        results
          ? undefined
          : `Sends from your own mailbox${admin?.zohoEmail ? ` (${admin.zohoEmail})` : ''}. Each person gets their own copy — nobody sees the others.`
      }
      onClose={sending ? () => undefined : onClose}
      footer={
        results ? (
          <button type="button" onClick={onClose} className="btn-primary py-2.5 px-5">
            Done
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="btn-secondary py-2.5 px-5 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !sendable.length}
              className="btn-primary py-2.5 px-5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending
                ? `Sending ${progress.done}/${progress.total}…`
                : `Send to ${sendable.length} ${sendable.length === 1 ? 'contact' : 'contacts'}`}
            </button>
          </>
        )
      }
    >
      {results ? (
        <div className="space-y-4">
          <Alert tone={failed ? 'warning' : 'success'}>
            Sent {sent} of {results.length}.
            {failed ? ` ${failed} failed.` : ''}
            {skipped ? ` ${skipped} skipped for having no email address.` : ''}
          </Alert>

          <ul className="max-h-72 space-y-1.5 overflow-y-auto text-sm">
            {results.map((result) => (
              <li
                key={result.email}
                className="flex items-start justify-between gap-3 rounded-lg border border-surface-border px-3 py-2"
              >
                <span className="min-w-0 text-white">
                  <span className="block truncate">{result.name || result.email}</span>
                  <span className="block truncate text-xs text-muted">{result.email}</span>
                </span>
                <span
                  className={`shrink-0 text-xs ${
                    result.success ? 'text-primary-400' : 'text-red-400'
                  }`}
                >
                  {result.success ? 'Sent' : result.error || 'Failed'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-5">
          {error && <Alert tone="error">{error}</Alert>}

          {withoutEmail > 0 && (
            <Alert tone="warning">
              {withoutEmail} of the {prospects.length} selected have no email address and
              will be skipped.
            </Alert>
          )}

          <div>
            <span className="mb-2 block text-sm text-muted">Recipients</span>
            <div className="max-h-32 overflow-y-auto rounded-lg border border-surface-border bg-surface p-3">
              {sendable.length === 0 ? (
                <p className="text-sm text-muted">Nobody selected has an email address.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {sendable.map((prospect) => (
                    <li key={prospect.id} className="truncate text-white">
                      {prospect.fullName}
                      {prospect.companyName && (
                        <span className="text-muted"> — {prospect.companyName}</span>
                      )}
                      <span className="text-muted/70"> · {prospect.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <span className="mb-2 block text-sm text-muted">Start from a template</span>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((template, index) => (
                <button
                  key={template.name}
                  type="button"
                  onClick={() => applyTemplate(index)}
                  className="rounded-lg border border-surface-border px-3 py-1.5 text-xs text-muted transition-colors hover:border-primary-500/40 hover:text-white"
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm text-muted">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Partnership opportunity with Nexasolutra"
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm text-muted">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              placeholder="Write your message. Each recipient is greeted by name automatically."
              className={`${inputClass} resize-y`}
            />
            <p className="mt-2 text-xs text-muted">
              Use <code className="text-primary-400">{'{{name}}'}</code>,{' '}
              <code className="text-primary-400">{'{{email}}'}</code> or{' '}
              <code className="text-primary-400">{'{{phone}}'}</code> anywhere to drop in
              each recipient's own details. Your signature is added for you.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm text-muted">
                Set next follow-up to
              </label>
              <input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                className={inputClass}
              />
            </div>
            <label className="flex items-start gap-3 pt-8 text-sm text-muted">
              <input
                type="checkbox"
                checked={markContacted}
                onChange={(e) => setMarkContacted(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#16A34A]"
              />
              <span>
                Mark as contacted, and move anyone still at Prospect to Contacted.
              </span>
            </label>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkEmailModal;
